import React, { Component, useEffect, useRef, useState, useMemo } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRectangleXmark } from '@fortawesome/pro-solid-svg-icons';
import { set } from 'lodash';
import NumberFormatter from '../../formatters/NumberFormatter';
import { generateGUID, score2color } from '../../helpers/utils';
import Streaming from "../../services/_realtime/price/Streaming";
import { faDown, faUp, faCircle } from '@fortawesome/pro-solid-svg-icons'
import './scoreFields.scss';
import { faInfo, faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import { Tooltip } from 'primereact/tooltip';
import { useTranslation } from 'react-i18next';

// Utility function to normalize indicator values
const normalizeIndicatorValue = (value) => {
    // Check if value is NaN string or actual NaN
    if (value === "NaN" || (typeof value === 'number' && isNaN(value)) ||
        value === undefined || value === null || value === '') {
        return 0; // Replace invalid values with 0
    }
    return value;
};

// Define component with named function to avoid display name warning
const ScoreFieldsComponent = (props) => {
    const { t } = useTranslation();
    const op = useRef(null);
    const streamingRef = useRef(null);
    const streamingUnSub = useRef(null);
    const numberFormatter = new NumberFormatter();
    const [selectedIndicatorItem, setSelectedIndicatorItem] = useState({ data: [], item: [] });
    const [data, setData] = useState(props.data || {});
    const [dailyChange, setDailyChange] = useState(0);
    const [lastPrice, setLastPrice] = useState(0);
    const [itemRealtimeData, setItemRealtimeData] = useState(null);
    const [indicatorsData, setIndicatorsData] = useState([]);
    const [hasRendered, setHasRendered] = useState(false);

    // Hata durumlarını işlemek için yeni bir callback ekleyelim
    const onError = props.onError || ((err) => console.error('ScoreFields error:', err));

    // Gösterge verilerini daha iyi takip etmek için yeni bir state ekleyelim
    const [dataProcessed, setDataProcessed] = useState(false);

    // İlk render edildiğini izlemek için yeni bir ref
    const firstRenderRef = useRef(true);

    // Fix the infinite render loop by memoizing props.data
    const memoizedData = useMemo(() => props.data, [
        // Only include specific properties that should trigger a re-render
        props.data?.entityId,
        // Use JSON.stringify for arrays to compare by value, not reference
        props.data?.indicators ? JSON.stringify(props.data.indicators.map(i => i.id)) : null
    ]);

    // Use this effect to initialize data from props - FIXED to prevent infinite loop
    useEffect(() => {
        try {
            if (!memoizedData) return;

            // Deep clone to prevent reference issues
            const safeData = JSON.parse(JSON.stringify(memoizedData || {}));

            // Ensure indicators is at least an empty array
            if (!safeData.indicators) {
                safeData.indicators = [];
            }

            // Compare with current data to avoid unnecessary updates
            const currentDataStr = JSON.stringify(data);
            const newDataStr = JSON.stringify(safeData);

            // Only update state if data has actually changed
            if (currentDataStr !== newDataStr) {
                setData(safeData);
            }

            // First render logic - only execute once
            if (firstRenderRef.current && memoizedData.entityId) {
                // console.log(`ScoreFields first render: ${memoizedData.code || memoizedData.entityId}`);
                firstRenderRef.current = false;

                // Request indicators data if missing
                if ((!memoizedData.indicators || memoizedData.indicators.length === 0) && memoizedData.entityId) {
                    console.log(`Missing indicators for ${memoizedData.code || memoizedData.entityId}, requesting update`);
                    // Use a timeout to ensure this doesn't create another render cycle immediately
                    setTimeout(() => {
                        const event = new CustomEvent("refreshSymbolData", {
                            detail: { entityId: memoizedData.entityId }
                        });
                        document.dispatchEvent(event);
                    }, 0);
                }
            }
        } catch (error) {
            console.error("ScoreFields data initialization error:", error);
            onError(error);
        }
    }, [memoizedData, onError]);

    // Use this effect to process indicator data with optimization
    useEffect(() => {
        try {
            if (data && data.indicators) {
                // Önceki gösterge verileriyle karşılaştırma yapma
                const prevIndicators = JSON.stringify(indicatorsData.map(i => i.fieldId || 'unknown'));
                const newIndicators = JSON.stringify((data.indicators || []).map(i => i.fieldId || 'unknown'));

                if (prevIndicators === newIndicators && indicatorsData.length > 0) {
                    return; // Göstergeler aynıysa işleme yapma
                }

                // Geçersiz değerleri 0 ile değiştirerek normalize et
                const normalizedIndicators = Array.isArray(data.indicators)
                    ? data.indicators.map(indicator => {
                        try {
                            return {
                                ...indicator,
                                score: normalizeIndicatorValue(indicator.score),
                                average: normalizeIndicatorValue(indicator.average),
                                last: normalizeIndicatorValue(indicator.last),
                                max: normalizeIndicatorValue(indicator.max),
                                min: normalizeIndicatorValue(indicator.min)
                            };
                        } catch (error) {
                            console.error("Error normalizing indicator:", indicator, error);
                            return indicator;
                        }
                    })
                    : [];

                setIndicatorsData(normalizedIndicators);
                setDataProcessed(true);

                // İlk kez veri işlediğimizde hasRendered'ı true yap
                if (normalizedIndicators.length > 0 && !hasRendered) {
                    setHasRendered(true);

                    // Eğer entityId varsa ve veri işlendiyse, bir event gönder
                    if (data.entityId) {
                        try {
                            const event = new CustomEvent("indicatorsRendered", {
                                detail: { entityId: data.entityId }
                            });
                            document.dispatchEvent(event);
                        } catch (eventError) {
                            console.error("Error dispatching indicatorsRendered event:", eventError);
                        }
                    }
                }
            } else {
                setIndicatorsData([]);
            }
        } catch (error) {
            console.error("Error processing indicators data:", error);
            onError(error);
            setIndicatorsData([]);
        }
    }, [data.indicators, hasRendered, onError]);

    const getBackgroundColorClass = (score) => {
        // Handle any remaining NaN values (should be caught by normalization)
        if (score === "NaN" || (typeof score === 'number' && isNaN(score)) ||
            score === undefined || score === null || score === '') {
            score = 0;
        }

        if (score >= 0 && score <= 1) return "score-1";
        else if (score > 1 && score <= 2) return "score-2";
        else if (score > 2 && score <= 3) return "score-3";
        else if (score > 3 && score <= 4) return "score-4";
        else if (score > 4 && score <= 5) return "score-5";
        else if (score > 5 && score <= 6) return "score-6";
        else if (score > 6 && score <= 7) return "score-7";
        else if (score > 7 && score <= 8) return "score-8";
        else if (score > 8 && score <= 9) return "score-9";
        else if (score > 9 && score <= 10) return "score-10";
        else return "score-default"; // Default for any values outside the range
    };

    const onClickIndicatorItem = (e, item, data) => {
        op.current.toggle(e);

        setSelectedIndicatorItem(state => ({
            ...state,
            data: [],
            item: [],
        }))

        setSelectedIndicatorItem(state => ({
            data: [...state.data, data],
            item: [...state.item, data.indicators],
        })
        )

        // setLastPrice(data.lastPrice);
        // setDailyChange(numberFormatter.Double(data.dailyChange, 2));
        setItemRealtimeData({ objectId: data.entityId, lastPrice: 0, dailyChange: 0, direction: faCircle });

        if (!streamingRef.current) {
            streamingRef.current = new Streaming();
        }

        if (streamingRef.current) {

            streamingUnSub.current = streamingRef.current.subscribe(
                { objectIds: [data.entityId] },
                (msg) => {
                    console.log(`${data["entityId"]} : `, msg);
                    setItemRealtimeData((prev) => {
                        let item = prev;
                        let newItem = null;


                        if (item.objectId === msg["@"]) {
                            const newLastPrice = 'lastPrice' in msg ? parseFloat(msg.lastPrice).toFixed(2) : item.lastPrice;
                            const newDailyChange = 'dailyChange' in msg ? parseFloat(msg.dailyChange).toFixed(2) : item.dailyChange;
                            const newDailyChangePrice = 'dailyChangePrice' in msg ? parseFloat(msg.dailyChangePrice).toFixed(2) : item.dailyChangePrice;
                            const isPricePositive = parseFloat(newDailyChangePrice) > 0;
                            const isIncreasedDailyChangePrice = parseFloat(newDailyChangePrice) > 0;
                            const isDecreasedDailyChangePrice = parseFloat(newDailyChangePrice) < 0;
                            const newAsk = 'ask' in msg ? parseFloat(msg.ask).toFixed(2) : item.ask;
                            const newBid = 'bid' in msg ? parseFloat(msg.bid).toFixed(2) : item.bid;
                            const newDailyHigh = 'dailyHigh' in msg ? parseFloat(msg.dailyHigh).toFixed(2) : item.dailyHigh;
                            const newDailyLow = 'dailyLow' in msg ? parseFloat(msg.dailyLow).toFixed(2) : item.dailyLow;
                            const newDailyVolume = 'dailyVolume' in msg ? numberFormatter.Double(msg.dailyVolume, 0) : item.dailyVolume;
                            const newLastTradeTime = 'lastTradeTime' in msg ? msg.lastTradeTime : item.lastTradeTime;
                            const newLastUpdateDate = 'lastUpdateDate' in msg ? msg.lastUpdateDate : item.lastUpdateDate;
                            const symbolName = 'symbolName' in msg ? msg.symbolName : item.symbolName;

                            newItem = {
                                ...item,
                                symbolName: symbolName,
                                ask: newAsk,
                                bid: newBid,
                                dailyHigh: newDailyHigh,
                                dailyLow: newDailyLow,
                                dailyVolume: newDailyVolume,
                                lastTradeTime: newLastTradeTime,
                                lastUpdateDate: newLastUpdateDate,
                                dailyChangePrice: newDailyChangePrice,
                                lastPrice: newLastPrice,
                                dailyChange: newDailyChange,
                                direction: isIncreasedDailyChangePrice ? faUp : (isDecreasedDailyChangePrice ? faDown : faCircle),
                                changeColor: isPricePositive ? 'green' : 'red'
                            };
                        }
                        else
                            newItem = item;
                        return newItem;
                    }
                    );
                }
            );

        }
    }

    // Optimize the indicator content rendering
    const indicatorsContent = useMemo(() => {
        try {
            // Hızlı çıkış durumları - önce kontrol et
            if (!data) {
                // console.log("ScoreFields: data nesnesi yok");
                return <div className="no-indicators">-</div>;
            }

            if (!data.indicators) {
                // console.log(`ScoreFields: ${data.code || 'Bilinmeyen'} için indicators dizisi yok`);
                return <div className="no-indicators">-</div>;
            }

            if (!Array.isArray(data.indicators)) {
                // console.log(`ScoreFields: ${data.code || 'Bilinmeyen'} için indicators bir dizi değil`);
                return <div className="no-indicators">-</div>;
            }

            if (data.indicators.length === 0) {
                // console.log(`ScoreFields: ${data.code || 'Bilinmeyen'} için indicators dizisi boş`);
                return <div className="no-indicators">-</div>;
            }

            // Veriyi güvenli bir şekilde sırala
            let sortedData;
            try {
                sortedData = [...data.indicators].sort((a, b) => {
                    if (!a.fieldName && !b.fieldName) return 0;
                    if (!a.fieldName) return 1;
                    if (!b.fieldName) return -1;
                    return (a.fieldName || '').localeCompare(b.fieldName || '');
                });
            } catch (sortError) {
                console.error("Error sorting indicators:", sortError);
                sortedData = data.indicators;
            }

            return (
                sortedData &&
                sortedData.map((item, index) => {
                    try {
                        const normalizedScore = normalizeIndicatorValue(item.score);
                        const backgroundColor = score2color(normalizedScore);

                        return (
                            <div
                                key={`${index}-${item.fieldId || 'unknown'}`}
                                className='indicatorsBodyTemplateColor'
                                style={{
                                    backgroundColor,
                                    alignContent: 'center',
                                    textAlign: 'center'
                                }}
                                onClick={(e) => onClickIndicatorItem(e, item, data)}
                            ></div>
                        );
                    } catch (itemError) {
                        console.error("Error rendering indicator item:", itemError);
                        return <div key={index} className='indicatorsBodyTemplateColor error'></div>;
                    }
                })
            );
        } catch (renderError) {
            console.error("Error rendering indicators content:", renderError);
            onError(renderError);
            return <div className="indicators-error">!</div>;
        }
    }, [data, data.indicators, onClickIndicatorItem]);

    const hideOverlayPanel = () => {
        if (streamingUnSub.current) {
            if (typeof streamingUnSub.current.Dispose === "function") {
                streamingUnSub.current.Dispose();
            } else {
                console.warn("ScoreFields: streamingUnSub.current.Dispose is not a function in hideOverlayPanel.", streamingUnSub.current);
            }
            streamingUnSub.current = null;
        }
    };

    useEffect(() => {
        return () => {
            if (streamingUnSub.current) {
                if (typeof streamingUnSub.current.Dispose === "function") {
                    streamingUnSub.current.Dispose();
                } else {
                    // Log details if Dispose is not a function but the ref exists
                    console.warn(
                        "ScoreFields: streamingUnSub.current.Dispose is not a function during unmount. Current value:",
                        streamingUnSub.current,
                        "Type:", typeof streamingUnSub.current
                    );
                }
                streamingUnSub.current = null;
            }
        };
    }, []);

    // İyileştirilmiş return değeri - boş durumlarda da düzgün görüntüleme
    return (
        <div className='indicatorsBodyTemplate'>
            {dataProcessed ? (
                indicatorsContent
            ) : (
                <div className="loading-indicators">
                    {/* İsteğe bağlı - yükleniyor göstergesi ekleyebilirsiniz */}
                    <div className="indicators-placeholder"></div>
                </div>
            )}
            <OverlayPanel
                key={props.data.entityId || 'unknown'}
                ref={op}
                
                breakpoints={{ '960px': '75vw', '640px': '100vw' }}
                style={{ width: '550px' }}
                className='indicatorsOverlayPanel p-0'
                onHide={hideOverlayPanel}
            >
                <Container className='indicatorsContainer'>
                    <Row className='header-row p-0 p-t-5 p-b-5' >

                        <div className='d-flex flex-row justify-content-between'>
                            {selectedIndicatorItem.data && selectedIndicatorItem.data.map((item, idx) => (
                                <div key={`${item.code || 'item'}-${idx}`} >
                                    <div className='w-100 roboto-bold-14 color-text'>{item.code}

                                        {/* <Tooltip target=".indicator-info-icon" className='indicator-info-icon-tooltip' position="top"
                                        >
                                            Son 4 Çeyrekliktir
                                        </Tooltip>
                                        <FontAwesomeIcon className='indicator-info-icon m-l-5' icon={faInfoCircle} /> */}
                                    </div>
                                    <div className='w-100 roboto-regular-12 color-text'>{itemRealtimeData && itemRealtimeData.symbolName} </div>
                                </div>
                            ))}

                            <div key="code" className='ml-auto m-r-15'>
                                <div className='w-100 roboto-bold-14 color-text'>{itemRealtimeData && itemRealtimeData.lastPrice} </div>
                                <div className={`roboto-medium-12 w-100 percent-change ${itemRealtimeData && itemRealtimeData?.changeColor}`}>%{itemRealtimeData && itemRealtimeData.dailyChange}</div>
                            </div>
                            <div className='header-close m-0'>
                                <FontAwesomeIcon onClick={(e) => op.current.toggle(e)} className='closeIcon' icon={faRectangleXmark} />

                            </div>
                        </div>
                    </Row>

                    <Row className=' p-t-10 p-b-10 tableHeader'>
                        <Col md='2' sm="2" className='roboto-medium-12 color-text'>
                        </Col>

                        <Col md='2' sm="2" className='roboto-medium-12 color-text'>
                        </Col>

                        <Col md='2' sm="2" className='roboto-medium-12 color-text text-align-right'>
                            {t('technicalAnalysis.minimum')}
                        </Col>

                        <Col md='2' sm="2" className='roboto-medium-12 color-text text-align-right'>
                            {t('technicalAnalysis.last')}
                        </Col>

                        <Col md='2' sm="2" className='roboto-medium-12 color-text text-align-right'>
                            {t('technicalAnalysis.average')}
                        </Col>

                        <Col md='2' sm="2" className='roboto-medium-12 color-text text-align-right'>
                            {t('technicalAnalysis.maximum')}
                        </Col>
                    </Row>

                    {indicatorsData &&
                        [...indicatorsData]
                            .sort((a, b) => {
                                // Sort by order field (if exists) or fallback to last value in descending order
                                if (a.order !== undefined && b.order !== undefined) {
                                    return a.order - b.order; // Ascending order by order field
                                }
                                // Fallback to previous sorting if order field doesn't exist
                                const lastA = parseFloat(normalizeIndicatorValue(a.last));
                                const lastB = parseFloat(normalizeIndicatorValue(b.last));
                                return lastB - lastA; // Descending order by last value
                            })
                            .map((item, index) => (
                            <Row className=' p-t-10 p-b-10 tableContent' key={`row-${item.fieldId || index}`}>
                                <Col md='1' sm="1" className='circle' style={{ backgroundColor: score2color(item.score) }} >
                                </Col>

                                <Col md='3' sm="3" className='roboto-medium-12 color-text '>
                                    {item.fieldName}
                                </Col>

                                <Col md='2' sm="2" className='roboto-medium-12 color-text text-align-right'>
                                    {numberFormatter.Double(item.min, 2)}
                                </Col>

                                <Col md='2' sm="2" className='roboto-medium-12 color-text text-align-right'>
                                    {numberFormatter.Double(item.last, 2)}
                                </Col>

                                <Col md='2' sm="2" className='roboto-medium-12 color-text text-align-right'>
                                    {numberFormatter.Double(item.average, 2)}
                                </Col>

                                <Col md='2' sm="2" className='roboto-medium-12 color-text text-align-right'>
                                    {numberFormatter.Double(item.max, 2)}
                                </Col>
                            </Row>
                        ))}
                    
                </Container >
            </OverlayPanel>
        </div >
    )
};

// Memoize the ScoreFieldsComponent to prevent unnecessary re-renders
// If you want to add a custom comparison, you can add a second argument
export default React.memo(ScoreFieldsComponent);